"use client"
import { useState, useEffect } from "react"
import styled from "styled-components"
import { motion, AnimatePresence } from "framer-motion"
import { FiAward, FiX, FiFilter, FiSearch, FiCheckCircle, FiGrid } from "react-icons/fi"

// --- STYLED COMPONENTS ---

const CertificatesPage = styled.div`
  min-height: 100vh;
  padding-top: 120px; /* Space for the navbar */
  padding-bottom: 6rem;
  background: transparent; // Let the global gradient show
`

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`

const PageHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 3rem;

  .icon-wrapper {
    display: inline-block;
    padding: 1rem;
    background: rgba(233, 196, 106, 0.15); // Accent color with low opacity
    border-radius: 50%;
    margin-bottom: 1rem;

    svg {
      color: #E9C46A; // Accent color
      display: block;
    }
  }

  h1 {
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 700;
    color: #1B1B1E;
    margin-bottom: 1rem;
    font-family: 'Poppins', sans-serif;

    .highlight {
      background: linear-gradient(135deg, #2A9D8F, #E9C46A);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: 1.125rem;
    color: #1B1B1E;
    opacity: 0.8;
    max-width: 800px;
    margin: 0 auto;
    line-height: 1.6;
  }
`

const FilterSection = styled(motion.div)`
  margin-bottom: 3rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(38, 70, 83, 0.15);
  border-radius: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;

  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #264653;
    opacity: 0.6;
  }

  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 3rem;
    background: transparent;
    border: 1px solid rgba(38, 70, 83, 0.2);
    border-radius: 0.5rem;
    color: #1B1B1E;
    font-size: 1rem;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: #2A9D8F;
      box-shadow: 0 0 0 3px rgba(42, 157, 143, 0.2);
    }
  }
`

const FilterDropdown = styled.div`
  select {
    width: 100%;
    padding: 0.75rem 1rem;
    background: transparent;
    border: 1px solid rgba(38, 70, 83, 0.2);
    border-radius: 0.5rem;
    color: #1B1B1E;
    font-size: 1rem;
    cursor: pointer;

    @media (min-width: 768px) {
        width: auto;
        min-width: 200px;
    }
  }
`

const CertificatesGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`

const CertificateCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(38, 70, 83, 0.15);
  border-radius: 1rem;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
    border-color: #E9C46A;
  }
`

const CertImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 1.414 / 1;
  background-size: cover;
  background-position: center;
  background-image: ${({ $image }) => `url(${$image})`};
  border-bottom: 1px solid rgba(38, 70, 83, 0.15);
`

const CertCardContent = styled.div`
  padding: 1.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`

const CertTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1B1B1E;
  margin-bottom: 0.5rem;
`

const IssuerName = styled.p`
  font-size: 0.9rem;
  color: #1B1B1E;
  opacity: 0.8;
`

const NoResults = styled(motion.div)`
  text-align: center;
  padding: 4rem 0;
  color: #1B1B1E;
  opacity: 0.7;
  h3 { font-size: 1.5rem; margin-bottom: 0.5rem; }
`

const StatsSection = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
`

const StatCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(38, 70, 83, 0.15);
  border-radius: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon-wrapper {
    font-size: 1.75rem;
    color: ${({ $color }) => $color};
  }
  
  .text-wrapper {
    text-align: left;
  }

  .number {
    font-size: 2rem;
    font-weight: 700;
    color: #1B1B1E;
    line-height: 1;
  }

  .label {
    color: #1B1B1E;
    opacity: 0.7;
    font-size: 0.9rem;
  }
`

// --- FULL MODAL STYLES (NO PLACEHOLDERS) ---

const ModalBackdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`

const ModalContent = styled(motion.div)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  img {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 0.5rem;
    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.4);
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: -15px;
  right: -15px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #F4F1DE;
  color: #1B1B1E;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  z-index: 1001;
`

// --- DATA ---
const certificatesData = [
  {
    title: "Software Development (Full Stack)",
    issuer: "kLab & ICT Chamber (TekHer Program)",
    image: "/klab.png",
    category: "Full Stack",
  },
  {
    title: "Back-end Application Development",
    issuer: "SheCanCODE & Mastercard Foundation",
    image: "/Shecancode.png",
    category: "Backend",
  },
  {
    title: "IoT Certification (LoRaWan)",
    issuer: "Hubiquitous & Fablab Rwanda",
    image: "/IOT.png",
    category: "IoT",
  },
  {
    title: "Entrepreneurship, ICT & Life Skills",
    issuer: "Hanga Hubs & ICT Chamber",
    image: "/ICT.png",
    category: "Business & ICT",
  },
];

const categories = ["All", ...new Set(certificatesData.map(c => c.category))];


// --- MAIN COMPONENT ---

const Certificates = () => {
  const [selectedCert, setSelectedCert] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    if (selectedCert) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [selectedCert]);

  const filteredCertificates = certificatesData.filter((cert) => {
    const matchesSearch =
      cert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.issuer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || cert.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <CertificatesPage>
        <Container>
          <PageHeader initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="icon-wrapper">
              <FiAward size={48} />
            </div>
            <h1>My <span className="highlight">Credentials</span></h1>
            <p>A browsable gallery of my professional certifications, demonstrating my commitment to continuous learning and skill development in the tech industry.</p>
          </PageHeader>

          <FilterSection initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <SearchBox>
              <FiSearch size={20} />
              <input type="text" placeholder="Search by title or issuer..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </SearchBox>
            <FilterDropdown>
              <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </FilterDropdown>
          </FilterSection>

          {filteredCertificates.length > 0 ? (
            <CertificatesGrid>
              {filteredCertificates.map((cert) => (
                <CertificateCard
                  key={cert.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  onClick={() => setSelectedCert(cert.image)}
                >
                  <CertImageContainer $image={cert.image} />
                  <CertCardContent>
                    <CertTitle>{cert.title}</CertTitle>
                    <IssuerName>Issued by: {cert.issuer}</IssuerName>
                  </CertCardContent>
                </CertificateCard>
              ))}
            </CertificatesGrid>
          ) : (
            <NoResults initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <FiSearch size={48} />
              <h3>No certificates found</h3>
              <p>Try adjusting your search terms or filters.</p>
            </NoResults>
          )}

          <StatsSection initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <StatCard $color="#264653">
              <div className="icon-wrapper"><FiGrid /></div>
              <div className="text-wrapper">
                <div className="number">{certificatesData.length}</div>
                <div className="label">Total Credentials</div>
              </div>
            </StatCard>
            <StatCard $color="#2A9D8F">
              <div className="icon-wrapper"><FiCheckCircle /></div>
              <div className="text-wrapper">
                <div className="number">{categories.length - 1}</div>
                <div className="label">Unique Categories</div>
              </div>
            </StatCard>
          </StatsSection>
        </Container>
      </CertificatesPage>

      <AnimatePresence>
        {selectedCert && (
          <ModalBackdrop onClick={() => setSelectedCert(null)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ModalContent onClick={(e) => e.stopPropagation()} initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8 }} transition={{ type: "spring", stiffness: 300, damping: 30 }}>
              <CloseButton onClick={() => setSelectedCert(null)}><FiX /></CloseButton>
              <img src={selectedCert} alt="Full size certificate" />
            </ModalContent>
          </ModalBackdrop>
        )}
      </AnimatePresence>
    </>
  );
};

export default Certificates;